import { HTMLAttributes } from 'react';

import { number, oneOf, string } from 'prop-types';

import { useDeviceType } from '@/hooks/useDeviceType';
import getAdUnit from '@/utils/ads/getAdUnit';
import { PageType as PageTypes } from '@/utils/ads/getAdUnit/types';
import { classMerge } from '@/utils/class-names';

import SidebarAd from './SidebarAd';

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {
	as?: 'div' | 'aside';
}

export interface AdSidebarProps extends SidebarProps {
	adPosition?: number;
	pageType: PageTypes;
	as?: 'div' | 'aside';
}

export const Sidebar = ({
	className,
	children,
	as: Tag = 'aside',
	...props
}: SidebarProps) => {
	const sidebarStyles = classMerge(
		'hidden flex-col col-span-4 col-start-9 items-center h-full lg:flex',
		className
	);

	return (
		<Tag data-cy="Sidebar" {...props} className={sidebarStyles}>
			{children}
		</Tag>
	);
};

/**
 * AdSidebar Library Component
 */
export const AdSidebar = ({
	className,
	children,
	adPosition = 1,
	pageType,
	as,
	...props
}: AdSidebarProps) => {
	const environment = 'dweb';

	const deviceType = useDeviceType();

	const isDesktop = deviceType === 'lg';

	if (!isDesktop) {
		return null;
	}

	const topId = `skyscraper-top-${adPosition}`;
	const midId = `skyscraper-mid-${adPosition}`;

	const topUnit = getAdUnit({
		pageType,
		environment,
		name: 'skyscraper-top-1',
	});

	const midUnit = getAdUnit({
		pageType,
		environment,
		name: 'skyscraper-mid-1',
	});

	return (
		<Sidebar as={as} {...props}>
			<SidebarAd id={topId} isSticky className="h-[33%]" adUnit={topUnit} />
			<SidebarAd id={midId} isSticky className="h-[67%]" adUnit={midUnit} />
		</Sidebar>
	);
};

AdSidebar.propTypes = {
	as: oneOf(['div', 'aside']),
	pageType: string,
	adPosition: number,
};
